import React, { useEffect, useMemo, useState } from "react";
import moment from "moment";
import { useDispatch, useSelector } from "react-redux";
import {
  createAttendanceHolidayAPI,
  deleteAttendanceHolidayAPI,
  getAttendanceHolidaysAPI,
  getAttendanceTeamWeekAPI,
  putAttendanceDayOverrideAPI,
} from "../../../Redux/Apis/attendanceApi";
import {
  canAttendanceTeamStatusEdit,
  isBuiltInAdminRole,
  isCeoOrCmoRoleUser,
} from "../../../app/utils/common/Functions";

const MANUAL_ON_LEAVE_PREFIX = "manual_on_leave::";
const MANUAL_LEAVE_KINDS = new Set(["full", "half", "early"]);

const encodeOnLeaveReason = ({ kind = "full", reason = "" } = {}) => {
  const kindKey = String(kind || "full").trim().toLowerCase();
  const normalizedKind = kindKey === "half_leave" || kindKey === "half"
    ? "half"
    : kindKey === "early_leave" || kindKey === "early"
      ? "early"
      : "full";
  const text = (reason || "").trim();
  return `${MANUAL_ON_LEAVE_PREFIX}${normalizedKind}${text ? `::${text}` : ""}`;
};

const decodeOnLeaveReason = (reason) => {
  const text = String(reason || "");
  return text
    .split("\n")
    .map((line) => {
      const trimmed = line.trim();
      const lower = trimmed.toLowerCase();
      const idx = lower.indexOf(MANUAL_ON_LEAVE_PREFIX);
      if (idx === -1) return line;
      const payload = trimmed.slice(idx + MANUAL_ON_LEAVE_PREFIX.length).trim();
      if (!payload) return "";
      const parts = payload.split("::");
      const firstPart = String(parts[0] || "").trim().toLowerCase();
      if (MANUAL_LEAVE_KINDS.has(firstPart)) {
        return parts.slice(1).join("::").trim();
      }
      return payload;
    })
    .filter((line) => String(line || "").trim().length > 0)
    .join("\n");
};

const decodeOnLeaveKind = (reason) => {
  const text = String(reason || "");
  for (const line of text.split("\n")) {
    const trimmed = line.trim();
    const lower = trimmed.toLowerCase();
    const idx = lower.indexOf(MANUAL_ON_LEAVE_PREFIX);
    if (idx === -1) continue;
    const payload = trimmed.slice(idx + MANUAL_ON_LEAVE_PREFIX.length).trim();
    if (!payload) return "full";
    const firstPart = String(payload.split("::")[0] || "").trim().toLowerCase();
    if (MANUAL_LEAVE_KINDS.has(firstPart)) return firstPart;
    return "full";
  }
  return "full";
};

const normalizeManualOnLeaveDisplayStatus = (displayStatus) => {
  const code = String(displayStatus?.code || "");
  const label = String(displayStatus?.label || "");
  const detail = String(displayStatus?.detail || "");
  const hasManualTag = detail.toLowerCase().includes(MANUAL_ON_LEAVE_PREFIX);
  if (!hasManualTag) return displayStatus || {};
  return {
    ...displayStatus,
    code: code === "absent" ? "on_leave" : code,
    label: label.toLowerCase() === "absent" ? "On leave" : label,
    detail: decodeOnLeaveReason(detail),
  };
};

const AttendanceTeamPanel = () => {
  const dispatch = useDispatch();
  const { teamWeek, teamWeekLoading, holidaysList } = useSelector((s) => s.attendance);
  const [weekStart, setWeekStart] = useState(() => moment().startOf("isoWeek").format("YYYY-MM-DD"));
  const [holidayForm, setHolidayForm] = useState({ date: "", label: "" });
  const [editCell, setEditCell] = useState(null);
  const [editReason, setEditReason] = useState("");
  const [manualStatus, setManualStatus] = useState("present");
  const [onLeaveKind, setOnLeaveKind] = useState("full");
  const canEditTeamStatus = canAttendanceTeamStatusEdit();

  useEffect(() => {
    dispatch(getAttendanceTeamWeekAPI({ weekStart }));
  }, [dispatch, weekStart]);

  useEffect(() => {
    dispatch(getAttendanceHolidaysAPI());
  }, [dispatch]);

  const employees = useMemo(
    () =>
      (teamWeek?.employees || []).filter(
        (emp) => !isCeoOrCmoRoleUser(emp) && !isBuiltInAdminRole(emp)
      ),
    [teamWeek?.employees]
  );
  const weekDates = teamWeek?.weekDates || [];
  const legend = teamWeek?.legend || {};

  const cellMap = useMemo(() => {
    const m = new Map();
    (teamWeek?.cells || []).forEach((c) => {
      m.set(`${c.userId}|${c.date}`, c);
    });
    return m;
  }, [teamWeek]);

  const prevWeek = () => {
    setWeekStart((s) => moment(s).subtract(1, "week").format("YYYY-MM-DD"));
  };
  const nextWeek = () => {
    setWeekStart((s) => moment(s).add(1, "week").format("YYYY-MM-DD"));
  };

  const saveOverride = () => {
    if (!editCell) return;
    const status = manualStatus || "present";
    const isLate = status === "late";
    const isAbsent = status === "absent";
    const isOnLeave = status === "on_leave";
    dispatch(
      putAttendanceDayOverrideAPI({
        userId: editCell.userId,
        workDate: editCell.date,
        isWfh: status === "wfh",
        isWfs: status === "wfs",
        isHalfDay: status === "half_day",
        lateReason: isLate ? (editReason?.trim() || "Marked late manually") : null,
        absentReason: isOnLeave
          ? encodeOnLeaveReason({ kind: onLeaveKind, reason: editReason })
          : isAbsent
            ? (editReason?.trim() || "Marked absent manually")
            : null,
      })
    ).then(() => {
      dispatch(getAttendanceTeamWeekAPI({ weekStart }));
      setEditCell(null);
    });
  };

  return (
    <div className="att-team">
      <div className="att-team__toolbar card-border card-body mb-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="text-lg font-semibold text-sky-900">Team attendance</div>
            <div className="text-xs text-[#6f7d97]">
              {teamWeek?.weekStart && teamWeek?.weekEnd
                ? `${moment(teamWeek.weekStart).format("MMM D")} – ${moment(teamWeek.weekEnd).format("MMM D, YYYY")}`
                : ""}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button type="button" className="button px-3 py-1" onClick={prevWeek}>
              ‹ Week
            </button>
            <button type="button" className="button px-3 py-1" onClick={nextWeek}>
              Week ›
            </button>
          </div>
        </div>
        <div className="att-legend mt-3 flex flex-wrap gap-3 text-xs">
          <span>
            <span className="att-legend-dot att-legend-dot--holiday" /> Holiday ({legend.holiday?.percent ?? 0}%)
          </span>
          <span>
            <span className="att-legend-dot att-legend-dot--ontime" /> On time ({legend.on_time?.percent ?? 0}%)
          </span>
          <span>
            <span className="att-legend-dot att-legend-dot--late" /> Late ({legend.late?.percent ?? 0}%)
          </span>
          <span>
            <span className="att-legend-dot att-legend-dot--absent" /> Absent ({legend.absent?.percent ?? 0}%)
          </span>
          <span>
            <span className="att-legend-dot att-legend-dot--leave" /> On leave ({legend.on_leave?.percent ?? 0}%)
          </span>
        </div>
      </div>

      <div className="att-team__holidays card-border card-body mb-4">
        <h4 className="mb-2 text-sm font-semibold">Company holidays</h4>
        <div className="flex flex-wrap gap-2 items-end">
          <input
            type="date"
            className="search-input"
            value={holidayForm.date}
            onChange={(e) => setHolidayForm((s) => ({ ...s, date: e.target.value }))}
          />
          <input
            className="search-input flex-1 min-w-[200px]"
            placeholder="Label (e.g. Annual Book Fair)"
            value={holidayForm.label}
            onChange={(e) => setHolidayForm((s) => ({ ...s, label: e.target.value }))}
          />
          <button
            type="button"
            className="button bg-btn-primary text-white px-4 py-2"
            onClick={() => {
              if (!holidayForm.date || !holidayForm.label.trim()) return;
              dispatch(createAttendanceHolidayAPI({ holidayDate: holidayForm.date, label: holidayForm.label.trim() })).then(() => {
                dispatch(getAttendanceHolidaysAPI());
                dispatch(getAttendanceTeamWeekAPI({ weekStart }));
                setHolidayForm({ date: "", label: "" });
              });
            }}
          >
            Add holiday
          </button>
        </div>
        <div className="mt-2 flex flex-wrap gap-2">
          {(holidaysList || []).slice(0, 12).map((h) => (
            <span key={h.id} className="att-holiday-chip">
              {moment(h.holidayDate).format("MMM D")}: {h.label}
              <button
                type="button"
                className="att-holiday-chip__x"
                onClick={() =>
                  dispatch(deleteAttendanceHolidayAPI(h.id)).then(() => {
                    dispatch(getAttendanceHolidaysAPI());
                    dispatch(getAttendanceTeamWeekAPI({ weekStart }));
                  })
                }
              >
                ×
              </button>
            </span>
          ))}
        </div>
      </div>

      <div className="att-team-grid-wrap card-border card-body">
        {teamWeekLoading ? (
          <div className="py-10 text-center text-sm">Loading grid…</div>
        ) : (
          <div className="att-team-grid-scroll">
            <table className="att-team-grid">
              <thead>
                <tr>
                  <th className="att-team-grid__emp">Employee</th>
                  {weekDates.map((d) => (
                    <th key={d}>
                      <div>{moment(d).format("ddd")}</div>
                      <div className="text-xs font-normal text-[#6f7d97]">{moment(d).format("D")}</div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {employees.map((emp) => (
                  <tr key={emp.userId}>
                    <td className="att-team-grid__emp">
                      <div className="flex items-center gap-2 flex-wrap">
                        {emp.profileImage ? (
                          <img src={emp.profileImage} alt="" className="w-8 h-8 rounded-full object-cover" />
                        ) : (
                          <div className="w-8 h-8 rounded-full bg-slate-200 text-xs flex items-center justify-center">
                            {(emp.firstname || "?")[0]}
                          </div>
                        )}
                        <span className="att-team-grid__emp-name">
                          {emp.firstname} {emp.lastname}
                        </span>
                        {emp.isExEmployee ? (
                          <span className="att-team-grid__ex-badge" title={emp.lastWorkingDate ? `Last working day: ${moment(emp.lastWorkingDate).format("DD MMM YYYY")}` : "Ex-employee"}>
                            Ex
                          </span>
                        ) : null}
                      </div>
                    </td>
                    {weekDates.map((d) => {
                      const c = cellMap.get(`${emp.userId}|${d}`);
                      const disp = normalizeManualOnLeaveDisplayStatus(c?.displayStatus || {});
                      const isPostExit = disp.code === "post_exit" || c?.afterLastWorkingDay;
                      return (
                        <td key={d}>
                          <button
                            type="button"
                            className={`att-team-cell att-team-cell--${disp.code || "unknown"}${isPostExit ? " att-team-cell--post-exit" : ""}`}
                            disabled={!canEditTeamStatus || isPostExit}
                            onClick={() => {
                              if (!canEditTeamStatus || isPostExit) return;
                              const encodedReason = c?.record?.absentReason || "";
                              const hasManualOnLeave = String(encodedReason)
                                .toLowerCase()
                                .includes(MANUAL_ON_LEAVE_PREFIX);
                              setEditCell({ userId: emp.userId, date: d, displayStatus: disp });
                              setEditReason(decodeOnLeaveReason(encodedReason));
                              setOnLeaveKind(decodeOnLeaveKind(encodedReason));
                              setManualStatus(
                                hasManualOnLeave
                                  ? "on_leave"
                                  : ["late", "absent", "on_leave", "wfh", "wfs", "half_day", "early_leave"].includes(disp.code)
                                    ? disp.code
                                    : "present"
                              );
                            }}
                            title={
                              isPostExit
                                ? emp.lastWorkingDate
                                  ? `After last working day (${moment(emp.lastWorkingDate).format("DD MMM YYYY")})`
                                  : "After employment ended"
                                : canEditTeamStatus
                                  ? "Edit day status"
                                  : "No permission to edit team status"
                            }
                          >
                            <div className="att-team-cell__main">{disp.label}</div>
                            {disp.detail ? <div className="att-team-cell__detail">{disp.detail}</div> : null}
                          </button>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {editCell ? (
        <div className="att-modal-overlay" role="presentation" onClick={() => setEditCell(null)}>
          <div className="att-modal card-border card-body !text-black" onClick={(e) => e.stopPropagation()}>
            <h4 className="font-semibold mb-2 !text-black">Edit reason</h4>
            <p className="text-xs text-[#6f7d97] mb-2">
              {moment(editCell.date).format("DD MMM YYYY")} · {editCell.displayStatus?.label}
            </p>
            <label className="block text-xs text-[#6f7d97] mb-1">Manual status</label>
            <select
              className="search-input w-full mb-3 !text-black border-black border-1 !bg-[#d2d2d238]"
              value={manualStatus}
              onChange={(e) => setManualStatus(e.target.value)}
            >
              <option value="present">Present</option>
              <option value="late">Late</option>
              <option value="absent">Absent</option>
              <option value="on_leave">On Leave</option>
              <option value="wfh">WFH (Work from home)</option>
              <option value="wfs">WFS (Work from site)</option>
              <option value="half_day">Half day (worked)</option>
            </select>
            {manualStatus === "on_leave" ? (
              <>
                <label className="block text-xs text-[#6f7d97] mb-1 mt-2">Leave type</label>
                <select
                  className="search-input w-full mb-3 !text-black border-black border-1 !bg-[#d2d2d238]"
                  value={onLeaveKind}
                  onChange={(e) => setOnLeaveKind(e.target.value)}
                >
                  <option value="full">Full day leave</option>
                  <option value="half">Half day leave</option>
                  <option value="early">Early leave</option>
                </select>
              </>
            ) : null}
            <textarea
              className="search-input w-full min-h-[80px] !bg-[#d2d2d238] !text-black"
              value={editReason}
              onChange={(e) => setEditReason(e.target.value)}
              placeholder="Reason (required for late/absent if your process needs it)"
            />
            <div className="flex gap-2 mt-3 justify-end">
              <button type="button" className="button px-4 py-2" onClick={() => setEditCell(null)}>
                Cancel
              </button>
              <button type="button" className="button bg-btn-primary text-white px-4 py-2" onClick={saveOverride}>
                Save
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default AttendanceTeamPanel;
